// 2026-05-09: shared voice-reactive gradient hook + helpers, lifted
// out of chat.js so the LEX AI Inbox view (lexAiInbox.js) can render
// the SAME voice-message bubble UI as the dating-app chat without
// pulling in the full chat.js module. The hook reads the audio
// element's frequency data via WebAudio AnalyserNode and exposes a
// CSS-variable bag (`--lx-voice-low/-high/-energy/-tilt`) that the
// .lxchat-voice-pill / -reactive rules consume to animate a soft
// gradient in time with the playback. Cross-origin sources fall back
// to plain element playback (no analyser wiring) since browsers
// silence WebAudio'd cross-origin streams.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const VOICE_VIZ_DEFAULT = Object.freeze({
  low: 0.2,
  high: 0.2,
  energy: 0.12,
  tilt: 0.5,
});

function clamp01(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  if (n <= 0) return 0;
  if (n >= 1) return 1;
  return n;
}

function avgBand(arr, from, to) {
  const a = Math.max(0, Math.floor(from));
  const b = Math.min(arr.length - 1, Math.floor(to));
  if (!arr?.length || b < a) return 0;
  let sum = 0;
  for (let i = a; i <= b; i += 1) sum += arr[i] || 0;
  return sum / (b - a + 1) / 255;
}

export function useVoiceReactiveGradient(audioRef) {
  const [viz, setViz] = useState(VOICE_VIZ_DEFAULT);
  const ctxRef = useRef(null);
  const sourceRef = useRef(null);
  const analyserRef = useRef(null);
  const binsRef = useRef(null);
  const rafRef = useRef(0);
  const smoothRef = useRef({
    low: VOICE_VIZ_DEFAULT.low,
    high: VOICE_VIZ_DEFAULT.high,
    energy: VOICE_VIZ_DEFAULT.energy,
    tilt: VOICE_VIZ_DEFAULT.tilt,
  });

  const stop = useCallback((teardown = false) => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    if (!teardown) return;
    try {
      sourceRef.current?.disconnect?.();
    } catch {}
    try {
      analyserRef.current?.disconnect?.();
    } catch {}
    try {
      ctxRef.current?.close?.();
    } catch {}
    sourceRef.current = null;
    analyserRef.current = null;
    binsRef.current = null;
    ctxRef.current = null;
  }, []);

  const animate = useCallback(() => {
    const analyser = analyserRef.current;
    const bins = binsRef.current;
    const el = audioRef.current;
    if (!analyser || !bins || !el) {
      stop(false);
      return;
    }
    analyser.getByteFrequencyData(bins);
    const n = bins.length || 0;
    const lowRaw = avgBand(bins, 0, n * 0.18);
    const highRaw = avgBand(bins, n * 0.5, n * 0.98);
    const midRaw = avgBand(bins, n * 0.2, n * 0.48);
    const energyRaw = clamp01(lowRaw * 0.38 + midRaw * 0.34 + highRaw * 0.45);
    const tiltRaw = clamp01(0.5 + (highRaw - lowRaw) * 0.9);

    const prev = smoothRef.current;
    const alpha = 0.24;
    const next = {
      low: prev.low + (lowRaw - prev.low) * alpha,
      high: prev.high + (highRaw - prev.high) * alpha,
      energy: prev.energy + (energyRaw - prev.energy) * alpha,
      tilt: prev.tilt + (tiltRaw - prev.tilt) * alpha,
    };
    smoothRef.current = next;
    setViz((old) => {
      if (
        Math.abs(old.low - next.low) < 0.006 &&
        Math.abs(old.high - next.high) < 0.006 &&
        Math.abs(old.energy - next.energy) < 0.006 &&
        Math.abs(old.tilt - next.tilt) < 0.006
      ) {
        return old;
      }
      return next;
    });

    if (!el.paused && !el.ended) {
      rafRef.current = requestAnimationFrame(animate);
    } else {
      stop(false);
    }
  }, [audioRef, stop]);

  const start = useCallback(async () => {
    const el = audioRef.current;
    if (!el || typeof window === "undefined") return;
    const mediaSrc = String(el.currentSrc || el.src || "").trim();
    if (mediaSrc) {
      try {
        const u = new URL(mediaSrc, window.location.href);
        const protocol = String(u.protocol || "").toLowerCase();
        const isLocalLike = protocol === "blob:" || protocol === "data:";
        const isCrossOrigin = u.origin && u.origin !== window.location.origin;
        if (!isLocalLike && isCrossOrigin) {
          stop(true);
          return;
        }
      } catch {}
    }
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      let ctx = ctxRef.current;
      if (!ctx || ctx.state === "closed") {
        ctx = new AC();
        ctxRef.current = ctx;
      }
      if (ctx.state === "suspended") {
        try {
          await ctx.resume();
        } catch {}
      }
      if (!analyserRef.current) {
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.82;
        analyserRef.current = analyser;
        binsRef.current = new Uint8Array(analyser.frequencyBinCount);
      }
      if (!sourceRef.current) {
        const source = ctx.createMediaElementSource(el);
        source.connect(analyserRef.current);
        analyserRef.current.connect(ctx.destination);
        sourceRef.current = source;
      }
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(animate);
      }
    } catch {}
  }, [audioRef, animate]);

  const reset = useCallback(
    (hard = true) => {
      smoothRef.current = {
        low: VOICE_VIZ_DEFAULT.low,
        high: VOICE_VIZ_DEFAULT.high,
        energy: VOICE_VIZ_DEFAULT.energy,
        tilt: VOICE_VIZ_DEFAULT.tilt,
      };
      setViz(VOICE_VIZ_DEFAULT);
      stop(hard);
    },
    [stop]
  );

  useEffect(() => () => stop(true), [stop]);

  const style = useMemo(
    () => ({
      "--lx-voice-low": clamp01(viz.low).toFixed(3),
      "--lx-voice-high": clamp01(viz.high).toFixed(3),
      "--lx-voice-energy": clamp01(viz.energy).toFixed(3),
      "--lx-voice-tilt": clamp01(viz.tilt).toFixed(3),
    }),
    [viz]
  );

  return { style, start, stop, reset };
}
